import { Box, Typography, Link, Divider } from "@mui/material";
import React, { useEffect } from "react";
import DocNavigation from '../components/DocNavigation';
import { useLocation } from 'react-router-dom';

const AnalyticsPage: React.FC = React.memo(() => {
    const location = useLocation();

    useEffect(() => {
        // Scroll to top smoothly when the page loads
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        // Handle hash navigation
        if (location.hash) {
            const id = location.hash.substring(1);
            const element = document.getElementById(id);
            if (element) {
                const headerOffset = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth"
                });
            }
        }
    }, [location.pathname, location.hash]);

    return (
        <Box sx={{ paddingLeft: 4 }}>
            <Typography 
                sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: "1.5rem", sm: "1.8rem" },
                    color: "#0b3452",
                    display: 'inline-block',
                    marginBottom: 0.5,
                    lineHeight: 1.5,
                }}
            >
               Funnel Analytics
            </Typography>

            {/* Customer-Level Insights Section */}
            <Box id="customer-level-insights">
                
                <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Real-Time Event Streaming of Finvu AA Journey
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    Introduction
                </Typography>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                Get visibility of the user actions on Finvu AA through our analytics solution that combines:
                </Typography>

                <Box component="ul" sx={{ mt: 1, color: "#555", pl: 3 }}>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Real-time event tracking via our JS SDK – <Link href="https://docs.finvu.in/web-sdk-integration#event-tracking" color="#1976d2" target="_blank" rel="noopener noreferrer">View Documentation</Link>
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Standardized response codes aligned with Sahamati's <Link href="https://sahamati.gitbook.io/aa-redirection-guidelines/1.4.0/specification/response-specification" color="#1976d2" target="_blank" rel="noopener noreferrer">Redirection Guidelines (v1.4.0)</Link>
                        </Typography>
                    </li>
                </Box>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    Benefits
                </Typography>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    With these analytics, you can:
                </Typography>

                <Box component="ul" sx={{ mt: 1, color: "#555", pl: 3 }}>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            ✅ <Box component="span" sx={{ fontWeight: 'bold' }}>Proactively Support Your Customers</Box> – Track real-time events and identify exactly where users are in their AA journey. If a user is stuck at the FIP discovery stage, you can instantly detect it and provide timely guidance—whether through in-app nudges or direct support.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            ✅ <Box component="span" sx={{ fontWeight: 'bold' }}>Enhance User Journeys</Box> – Use termination response codes to dynamically tailor the next steps. Guide users who face issues, fast-track successful ones, and build trust with personalized prompts—all with minimal effort.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            ✅ <Box component="span" sx={{ fontWeight: 'bold' }}>Boost Success Rates</Box> – Identify friction points, analyze user behavior, and make rapid improvements to optimize your conversion rates.
                        </Typography>
                    </li>
                </Box>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    How It Works (And Who It's For)
                </Typography>
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    These analytics are available for all Financial Information Users (FIU) partners leveraging our JavaScript SDK based Finvu AA Client. This Finvu AA client can be:
                </Typography>

                <Box component="ul" sx={{ mt: 1, color: "#555", pl: 3 }}>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>Sweecar (Safe Web Environment for Encrypted Consent Approval with Redirection) clients</Box>: Fully designed and developed by Finvu AA, with theme, logo, and font customizations, adhering to Sahamati Redirection Guidelines.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>Non Sweecar clients</Box>: Finvu AA Clients that use our JS SDK and are jointly developed with our FIU/TSP Partners’ design and engineering inputs 
                        </Typography>
                    </li>
                </Box>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    Event Tracking
                </Typography>
                
                <Box component="ul" sx={{ mt: 1, color: "#555", pl: 3 }}>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Built directly into Finvu's JS SDK (Auto-Updated for all FIUs)
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Transmits real-time events from the AA client to your parent app using iframe's postMessage method
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Supports custom event configuration and aliasing of default events
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Requires zero additional setup
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ lineHeight: 1.7 }}>
                            Available to all FIUs using either Sweecar or Non Sweecar AA client.
                        </Typography>
                    </li>
                </Box>

                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                    <Link href="https://docs.finvu.in/web-sdk-integration#event-tracking" color="#1976d2" target="_blank" rel="noopener noreferrer">View Documentation</Link>
                </Typography>

                <Divider sx={{ mt: 5, mb: 3 }} />

                <Typography variant="subtitle1" sx={{ mt: 3, color: "#0b3452", fontWeight: "bold" }}>
                    Response Codes
                </Typography>
                
                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    If you are using Sweecar AA client, you will receive standardized response codes (as defined by Sahamati) when a user's AA journey ends and they return to your app. These response codes provide precise insights into the user's last completed step before exiting.
                </Typography>

                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontSize: "1rem" }}>
                    Finvu currently supports all response codes <b>except 2, 3, 4, and 6</b> from Sahamati's response specification.
                </Typography>

                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7 }}>
                    <Link href="https://sahamati.gitbook.io/aa-redirection-guidelines/1.4.0/specification/response-specification" color="#1976d2" target="_blank" rel="noopener noreferrer">View Sahamati's Redirection Guidelines (Response Specifications) (v1.4.0)</Link>
                </Typography>

                <Typography sx={{ mt: 2, color: "#555", lineHeight: 1.7, fontStyle: 'italic', fontWeight: 'bold' }}>
                    *Note: The Sahamati response codes are available only to the FIUs who are currently on v2 (and onwards) of our redirection Finvu AA client (Sweecar). 
                </Typography>

                <Box sx={{ mt: 4, mb: 3, py: 2, px: 3, backgroundColor: "#f8f9fa", borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Typography sx={{ color: "#555", lineHeight: 1.7, textAlign: "center" }}>
                        Reach out at <Link href="mailto:support@cookiejar.co.in" color="#1976d2">support@cookiejar.co.in</Link> for a quick demo and to start knowing your customers more now!
                    </Typography>
                </Box>

               

            </Box>
            <DocNavigation currentPath={location.pathname} />
        </Box>
    );
});

export default AnalyticsPage; 